import {
  GeneralPackageInfo,
  GridCell,
  GridSelection,
  GridSubject,
  OptionsGridModel,
  OptionsOrderResult,
  OptionsResponse,
  PackageResult,
  SubscriptionTypeInfo,
} from './types';

/** Filtered by duration, not name, so a renamed subscription type cannot break it. */
export const DISPLAYED_DURATIONS = [1, 3];

type BuildOptions = {
  allowedDurations?: number[];
};

/**
 * Turns an unanchored options response into a renderable grid. Each cell is resolved to its
 * package via its first order number; unresolvable cells and empty subjects are dropped.
 */
export const buildOptionsGrid = (response: OptionsResponse, options: BuildOptions = {}): OptionsGridModel => {
  const allowedDurations = options.allowedDurations || DISPLAYED_DURATIONS;
  const ordersByNumber = indexOrders(response.orderResults || []);
  const subjectInfo = new Map((response.subjects || []).map((s) => [s.subjectsId, s]));
  const generalPackages = response.generalPackages || [];
  const subscriptionTypes = (response.subscriptionTypes || [])
    .filter((t) => allowedDurations.indexOf(t.subscriptionTypesDuration) !== -1)
    .sort((a, b) => a.subscriptionTypesDuration - b.subscriptionTypesDuration);

  const subjects: GridSubject[] = [];

  for (const subjectOptions of response.subjectOptions || []) {
    const cells: GridCell[] = [];

    for (const cell of subjectOptions.cells || []) {
      const orderNumber = (cell.orderNumbers || [])[0];
      const order = orderNumber !== undefined ? ordersByNumber.get(orderNumber) : undefined;
      const pkg = order ? findPackage(order, subjectOptions.subjectsId, cell.generalPackagesId, cell.subscriptionTypesId) : undefined;

      if (!pkg || allowedDurations.indexOf(pkg.subscriptionTypesDuration) === -1) {
        continue;
      }

      cells.push({
        generalPackagesId: cell.generalPackagesId,
        subscriptionTypesId: cell.subscriptionTypesId,
        packagesId: pkg.packagesID,
        orderNumber,
        package: pkg,
      });
    }

    if (cells.length === 0) {
      continue;
    }

    const tierIds = new Set(cells.map((c) => c.generalPackagesId));
    const lengthIds = new Set(cells.map((c) => c.subscriptionTypesId));
    const info = subjectInfo.get(subjectOptions.subjectsId);
    const firstPackage = cells[0].package;

    // A tier or length missing from the response lists is rebuilt from the package itself.
    const tiers: GeneralPackageInfo[] = generalPackages.filter((g) => tierIds.has(g.generalPackagesId));
    cells.forEach((c) => {
      if (!tiers.some((t) => t.generalPackagesId === c.generalPackagesId)) {
        tiers.push({ generalPackagesId: c.generalPackagesId, generalPackagesName: c.package.generalPackagesName });
      }
    });

    const lengths: SubscriptionTypeInfo[] = subscriptionTypes.filter((t) => lengthIds.has(t.subscriptionTypesId));
    cells.forEach((c) => {
      if (!lengths.some((l) => l.subscriptionTypesId === c.subscriptionTypesId)) {
        lengths.push({
          subscriptionTypesId: c.subscriptionTypesId,
          subscriptionTypesName: c.package.subscriptionTypesName,
          subscriptionTypesDuration: c.package.subscriptionTypesDuration,
        });
      }
    });
    lengths.sort((a, b) => a.subscriptionTypesDuration - b.subscriptionTypesDuration);

    subjects.push({
      subjectsId: subjectOptions.subjectsId,
      subjectsName: info ? info.subjectsName : firstPackage.subjectsName,
      subjectsShortName: info ? info.subjectsShortName : firstPackage.subjectsShortName,
      bMultipleGeneralPackages: tierIds.size > 1,
      bMultipleSubscriptionTypes: lengthIds.size > 1,
      tiers,
      lengths,
      cells,
    });
  }

  const lengths: SubscriptionTypeInfo[] = [];
  subjects.forEach((s) =>
    s.lengths.forEach((l) => {
      if (!lengths.some((x) => x.subscriptionTypesId === l.subscriptionTypesId)) lengths.push(l);
    })
  );
  lengths.sort((a, b) => a.subscriptionTypesDuration - b.subscriptionTypesDuration);

  return {
    bMultipleSubjects: subjects.length > 1,
    subjects,
    lengths,
  };
};

export const findCell = (subject: GridSubject, generalPackagesId: number, subscriptionTypesId: number): GridCell | undefined =>
  subject.cells.find((c) => c.generalPackagesId === generalPackagesId && c.subscriptionTypesId === subscriptionTypesId);

export const findCellByPackage = (subject: GridSubject, packagesId: number): GridCell | undefined =>
  subject.cells.find((c) => c.packagesId === packagesId);

/** First preferred package the grid actually offers, per subject. Never selects an ineligible package. */
export const resolveDefaultSelection = (
  grid: OptionsGridModel,
  preferences: { [subjectsId: number]: (number | null | undefined)[] }
): GridSelection => {
  const selection: GridSelection = {};

  for (const subject of grid.subjects) {
    const preferred = preferences[subject.subjectsId] || [];
    const match = preferred.find((id) => id !== null && id !== undefined && findCellByPackage(subject, id) !== undefined);

    if (match !== undefined && match !== null) {
      selection[subject.subjectsId] = match;
    }
  }

  return selection;
};

export const packageIdsInGrid = (grid: OptionsGridModel): number[] => {
  const ids: number[] = [];
  grid.subjects.forEach((s) => s.cells.forEach((c) => ids.push(c.packagesId)));
  return ids;
};

/** null packagesId deselects the subject. */
export const applySelection = (selection: GridSelection, subjectsId: number, packagesId: number | null): GridSelection => {
  const next: GridSelection = { ...selection };

  if (packagesId === null) {
    delete next[subjectsId];
  } else {
    next[subjectsId] = packagesId;
  }

  return next;
};

export const lengthLabel = (cell: GridCell): string => {
  const years = cell.package.subscriptionTypesDuration;
  return years === 1 ? '1 year' : `${years} years`;
};

export const pricePerYearLabel = (net: number, years: number): string => `Price per year £${(net / years).toFixed(2)}`;

/** Package ids in ascending subject id order. */
export const selectionToPackageIds = (selection: GridSelection): number[] =>
  Object.keys(selection)
    .map((k) => Number(k))
    .sort((a, b) => a - b)
    .map((subjectsId) => selection[subjectsId]);

const indexOrders = (orders: OptionsOrderResult[]) => {
  const map = new Map<number, OptionsOrderResult>();
  orders.forEach((o) => map.set(o.orderNumber, o));
  return map;
};

const findPackage = (
  order: OptionsOrderResult,
  subjectsId: number,
  generalPackagesId: number,
  subscriptionTypesId: number
): PackageResult | undefined =>
  (order.orderDiscountResult?.packageResults || []).find(
    (p) => p.subjectsID === subjectsId && p.generalPackagesID === generalPackagesId && p.subscriptionTypesID === subscriptionTypesId
  );
